/**
 * Legal content for PCOS Insights — Privacy Policy + Terms of Service.
 *
 * Written for a health-tech application collecting health information.
 * Content is intentionally plain-language, editorial, and covers:
 *   • what we collect
 *   • how we use it
 *   • how it's stored / protected
 *   • your rights
 *   • medical disclaimer
 *   • contact
 *
 * This is a good-faith template — not legal advice. You should have
 * this reviewed by a qualified attorney before public launch in a
 * regulated market (US HIPAA · EU GDPR · India DPDP).
 */

const APP_NAME = 'PCOS Insights';
const CONTACT_EMAIL = 'hello@pcosinsights.com';
const LAST_UPDATED = 'February 2026';

export const PRIVACY_POLICY = {
  title: 'Privacy Policy',
  lastUpdated: LAST_UPDATED,
  sections: [
    {
      h: 'Who we are',
      p: `${APP_NAME} is a health-tech product that helps people with (or exploring) PCOS understand their cycles, hormones and symptoms. This policy explains what personal and health data we collect, why, and how we protect it.`,
    },
    {
      h: 'Information we collect',
      p: 'We collect only the data needed to make the product work for you:',
      list: [
        'Account information: name, email, and contact number.',
        'Health information you log: cycle dates, symptoms, mood, energy, hormone report values, medications and lifestyle notes.',
        'Assessment responses: age band, diagnosis status, cycle regularity and your primary goal.',
        'Device information: browser type, OS and the device identifier used for push notifications (if you enable them).',
        'Usage information: which screens you open, so we can improve the product.',
      ],
    },
    {
      h: 'How we use your information',
      p: 'Your data is used to:',
      list: [
        'Generate your personalized Cycle experience, Today\u2019s Intelligence and Theo insights.',
        'Send you notifications and reminders you\u2019ve opted into.',
        'Improve product quality and detect health patterns for your benefit.',
        'Respond to account and support requests.',
      ],
    },
    {
      h: 'What we never do',
      list: [
        'We never sell your personal or health information to any third party.',
        'We never share your identifiable data with advertisers.',
        'We never use your data to train external AI models.',
      ],
    },
    {
      h: 'Where your data lives',
      p: 'Health data is stored in Supabase (Postgres) with row-level security so that only you can read your own rows. Traffic is encrypted in transit (TLS). Access is logged. Emergent LLM keys used by Theo do not retain your prompts.',
    },
    {
      h: 'Your rights',
      p: 'You can, at any time:',
      list: [
        'Access all data we hold about you.',
        'Correct or update any information.',
        'Delete your account and every record we hold, permanently.',
        'Withdraw consent for notifications or contact number use.',
      ],
      after: `To exercise any of these rights, contact ${CONTACT_EMAIL}. We\u2019ll respond within 30 days.`,
    },
    {
      h: 'Data retention',
      p: 'We keep your data for as long as your account is active. If you delete your account, all identifiable health records are erased within 30 days. Aggregated, non-identifying analytics may be retained to improve the product.',
    },
    {
      h: 'Children',
      p: `${APP_NAME} is not designed for users under 13. If you believe a minor has provided data to us, please contact ${CONTACT_EMAIL} and we\u2019ll delete it.`,
    },
    {
      h: 'Medical disclaimer',
      p: `${APP_NAME} provides educational insights and pattern detection. It is not a medical device and does not diagnose, treat or replace professional medical advice. Always consult a qualified clinician for medical concerns.`,
    },
    {
      h: 'Changes to this policy',
      p: 'We\u2019ll notify you in-app before any material change. Continued use after the change means you accept the update.',
    },
    {
      h: 'Contact',
      p: `Questions or concerns: ${CONTACT_EMAIL}.`,
    },
  ],
};

export const TERMS_OF_SERVICE = {
  title: 'Terms of Service',
  lastUpdated: LAST_UPDATED,
  sections: [
    {
      h: 'Acceptance',
      p: `By creating an account or using ${APP_NAME}, you agree to these Terms. If you don\u2019t agree, please stop using the service.`,
    },
    {
      h: 'Your account',
      list: [
        'You must be at least 13 years old to use the service.',
        'You\u2019re responsible for keeping your login credentials secure.',
        'You agree to provide accurate information when creating your account.',
      ],
    },
    {
      h: 'Health content',
      p: `${APP_NAME} provides personalized insights based on the data you log. The insights are for informational purposes only. They are not a medical diagnosis, prescription or treatment plan. Always consult a licensed healthcare provider before making decisions about your health.`,
    },
    {
      h: 'Acceptable use',
      p: 'You agree not to:',
      list: [
        'Attempt to access another user\u2019s account or data.',
        'Reverse-engineer, scrape or tamper with the service.',
        'Use the service for any unlawful purpose.',
        'Impersonate another person.',
      ],
    },
    {
      h: 'Content ownership',
      p: 'You own the health data you log. By using the service, you grant us permission to store and process it as described in the Privacy Policy so we can deliver personalized insights to you.',
    },
    {
      h: 'AI-generated content',
      p: `${APP_NAME}\u2019s companion, Theo, uses AI to generate insights based on your logged data. AI output can occasionally be incorrect or incomplete. Treat all AI-generated content as informational only.`,
    },
    {
      h: 'Subscriptions & payments',
      p: 'Some features may require a paid subscription. Pricing, billing terms, and cancellation policies will be disclosed at the point of purchase. Refunds follow the applicable app-store policy.',
    },
    {
      h: 'Termination',
      p: 'You can delete your account at any time. We may suspend or terminate accounts that violate these Terms.',
    },
    {
      h: 'Limitation of liability',
      p: `To the fullest extent permitted by law, ${APP_NAME} is not liable for indirect, consequential, or health-related damages arising from your use of the service. Your only remedy for dissatisfaction is to stop using the service.`,
    },
    {
      h: 'Changes to these terms',
      p: 'We\u2019ll notify you in-app before material changes. Continued use after the change means you accept the update.',
    },
    {
      h: 'Contact',
      p: `Questions about these Terms: ${CONTACT_EMAIL}.`,
    },
  ],
};
